<template>
  <div class="container-lg mt-2">

    <!-- Product Breadcrumbs Component -->
    <product-breadcrumbs :in18="in18" :product="product"/>

    <div class="single">

      <!-- Product Preview Gallery Component -->
      <product-preview-gallery :in18="in18" :product="product"/>

      <div class="single__content">
        <!-- Product Title -->
        <h1 class="single__title">{{ product.title }}</h1>
        <!-- Product Data -->
        <ul class="product__data">
          <li v-if="product.manufacture" class="product_data-item">
            {{ in18['page_manufacturer'] }}
            <span class="text-primary fw-500">
              <a :href="product.manufacture.link" :title="product.manufacture.title">
                {{ product.manufacture.title }}
              </a>
            </span>
          </li>

          <li v-if="product.code" class="product_data-item">
            {{ in18['page_product_code'] }}
            <span class="fw-500">
              {{ product.code }}
            </span>
          </li>

          <li class="product_data-item">
            {{ in18['page_availability'] }}
            <span :class="product.stock_status_class">
              {{ product.stock_status }}
            </span>
          </li>
        </ul><!-- /.product__data -->

        <!-- Product Price Component -->
        <product-price
            :class-name="'single__price'"
            :prices="product.prices"
        />

        <!-- Short Description -->
        <div v-if="product.short_description" class="single__text">
          <div :class="{'expanded': showReadMoreLink }">{{ displayedDescription }}</div>
          <a v-if="showReadMoreLinkButton" :title="in18['read_more_label']" class="link link--primary fw-500" href="#"
             @click.prevent="toggleReadMoreLink">
            {{ in18['read_more_label'] }}
          </a>
        </div><!-- /.single__text -->

        <!-- Product Footer -->
        <div class="single__footer">
          <div class="single__footer-add">
            <!-- Product Counter Component -->
            <product-counter
                @update-select-quantity="updateSelectQuantity"
            />
            <!-- Add to Cart Button Component -->
            <add-to-cart-button
                :in18="in18"
                :product-id="product.id"
                :select-quantity="selectQuantity"
                :title="in18['page_add_to_cart']"
            />
          </div>
          <div class="single__footer-btns">
            <!-- Add to Wishlist Button Component -->
            <add-to-wishlist-button :class-name="'btn  btn--square'" :in18="in18" :product-id="product.id"/>
            <!-- Add to Compare List Button Component -->
            <add-to-compare-list-button :class-name="'btn  btn--square'" :in18="in18" :product-id="product.id"/>
          </div>

          <template v-if="product.quantity === 0">
            <!-- Action buttons -->
            <h3 class="single__footer-title">
              {{ in18['page_you_can_leave_your_email_address'] }}
            </h3>

            <!-- Subscribe Form -->
            <div class="subscribe subscribe__form my-0 p-0">
              <!-- Subscriber Email Input -->
              <input v-model="subscriberEmail"
                     :placeholder="in18['attribute_enter_email']"
                     class="subscribe__input"
                     type="email"
              >
              <!-- Subscribe Button -->
              <button :disabled="isDisableSubscribeBtn"
                      class="btn btn--green mb-0"
                      type="submit"
                      @click="subscribeToProduct">
                {{ in18['button_subscribe'] }}
              </button>
              <!-- Google reCAPTCHA -->
              <google-recaptcha-v3
                  id="register_us_id"
                  ref="captcha"
                  v-model="gRecaptchaResponse"
                  :site-key="captcha_site_key"
                  action="product_subscribe_action"
                  corner
              />
            </div>
          </template>
        </div><!-- /.single__footer -->


        <!-- Product Variant Slider -->
        <template v-if="product.productVariantSlider && product.productVariantSlider.length > 0">
          <h3 class="variations__title">{{ in18['page_variations'] }}</h3>
          <div id="productVariantThumbs" class="single__thumbs product-variants-slider">
            <div v-for="slide in product.productVariantSlider" class="slider-item lg:w-[112px] h-[74px] lg:h-[112px] overflow-hidden">
              <a :href="slide.link">
                <picture>
                  <source :srcset="slide.icon.webp" type="image/webp">
                  <img :alt="slide.title" :src="slide.icon?.source" class="w-full h-full object-contain object-center">
                </picture>
              </a>
            </div>
          </div>
        </template>
        <!-- end of Product Variant Slider -->

        <!-- Product Credit Prices Component -->
<!--        <product-credit-prices :in18="in18" :product="product"/>-->

        <template v-if="product.quantity > 0">
          <ul class="delivery-options">
            <li v-if="shipping_in_shop_is_active" class="delivery-options__item">
              <div class="delivery-options__wrapper">
                <div class="delivery-options_icon">
                  <img alt="" class="img" src="/shop/images/delivery-option-1-1.svg">
                </div>
                <div class="delivery-options__info">
                  <div class="delivery-options__title">{{ in18['delivery_in_shop_title'] }}</div>
                  <div class="delivery-options__text">{{ in18['delivery_in_shop_time'] }}</div>
                  <div class="delivery-options_price">{{ in18['delivery_in_shop_price'] }}</div>
                </div>
              </div>
            </li>
            <li class="delivery-options__item">
              <div class="delivery-options__wrapper">
                <div class="delivery-options_icon">
                  <img alt="" class="img" src="/shop/images/delivery-option-2-1.svg">
                </div>
                <div class="delivery-options__info">
                  <div class="delivery-options__title">{{ in18['delivery_courier_title'] }}</div>
                  <div class="delivery-options__text">{{ in18['delivery_courier_time'] }}</div>
                  <div class="delivery-options_price">{{ in18['delivery_courier_price'] }}</div>
                </div>
              </div>
            </li>
            <li class="delivery-options__item">
              <div class="delivery-options__wrapper">
                <div class="delivery-options_icon">
                  <img alt="" class="img" src="/shop/images/delivery-option-3-1.svg">
                </div>
                <div class="delivery-options__info">
                  <div class="delivery-options__title">{{ in18['delivery_locker_title'] }}</div>
                  <div class="delivery-options__text">{{ in18['delivery_locker_time'] }}</div>
                  <div class="delivery-options_price">{{ in18['delivery_locker_price'] }}</div>
                </div>
              </div>
            </li>
          </ul><!-- /.delivery-options -->
          <div class="delivery_dates_message text-sm text-gray text-end mb-3 mt-2">
            {{ in18['page_estimated_delivery_dates_message'] }}
          </div>
        </template>
        <esto-credit-widget v-if="product.show_esto_widget" />
      </div>
    </div><!-- /.single -->
  </div><!-- /.container -->
</template>

<script>

import ProductBreadcrumbs from "./ProductBreadcrumbs.vue";
import ProductPreviewGallery from "./ProductPreviewGallery.vue";
import ProductCreditPrices from "./ProductCreditPrices.vue";
import ProductCounter from "./ProductCounter";
import AddToWishlistButton from "./AddToWishlistButton.vue";
import AddToCompareListButton from "./AddToCompareListButton.vue";
import AddToCartButton from "./AddToCartButton.vue";
import EstoCreditWidget from "./EstoCreditWidget.vue";
import {isValidEmail} from "../../helpers/is-valid-email";

export default {
  name:       'ProductHeader',
  components: {
    'product-preview-gallery':    ProductPreviewGallery,
    'product-breadcrumbs':        ProductBreadcrumbs,
    'product-credit-prices':      ProductCreditPrices,
    'product-counter':            ProductCounter,
    'add-to-wishlist-button':     AddToWishlistButton,
    'add-to-compare-list-button': AddToCompareListButton,
    'add-to-cart-button':         AddToCartButton,
    'esto-credit-widget':         EstoCreditWidget,

  },
  props:      {
    product:                    Object,
    in18:                       {
      type: Object,
    },
    captcha_site_key:           String,
    shipping_in_shop_is_active: Boolean,
  },
  data() {
    return {
      showReadMoreLink:          false,
      subscriberEmail:           '',
      gRecaptchaResponse:        null,
      selectQuantity:            1,
      selectedVariation:         this.product,
      maxShortDescriptionLength: 300,
      productVariantThumbs:  null
    }
  },

  mounted() {
    ga_view_item_event(this.product, 0, this.product.prices.current_price);

    if (this.productVariantThumbs) {
      this.productVariantThumbs.slick('unslick')
    }

    this.productVariantThumbs = $('#productVariantThumbs').slick({
      infinite:       true,
      slidesToShow:   4,
      slidesToScroll: 1,
      fade:           false,
      arrows:         true,
      focusOnSelect:  true,
      lazyLoad:       'ondemand',
    })
  },

  computed: {

    /**
     * Checks whether the subscribe button should be disabled based on the validity of the subscriber's email.
     * @returns {boolean} True if the subscribe button should be disabled, otherwise false.
     */
    isDisableSubscribeBtn() {
      return !isValidEmail(this.subscriberEmail);
    },

    /**
     * Checks whether the "Read More" link button should be displayed.
     * @returns {boolean} True if the "Read More" link button should be displayed, otherwise false.
     */
    showReadMoreLinkButton() {
      // Show the "Read More" link button if the short description exceeds the maximum length and is not expanded.
      return !this.showReadMoreLink && this.product.short_description.length > this.maxShortDescriptionLength;
    },

    /**
     * Determines the displayed description, either full or truncated, based on the "Read More" link state.
     * @returns {string} The displayed short description.
    f */
    displayedDescription() {
      if (this.product.short_description.length > this.maxShortDescriptionLength) {
        return this.showReadMoreLink
            ? this.product.short_description
            : this.product.short_description.slice(0, this.maxShortDescriptionLength) + '...';
      } else {
        return this.product.short_description;
      }
    },
  },

  watch: {
    product(newproduct) {
      this.selectedVariation = newproduct;
    },

    selectedVariation(val) {
      this.$emit('change-active-product', val);
      this.selectQuantity = 1;
      // Committing the quantity change to the Vuex store
      this.$store.commit('SET_QUANTITY', 1);
      this.subscriberEmail = ''; // Clearing subscriber email when the product details are updated.
    },
  },

  emits: [
    'change-active-product'
  ],

  methods: {

    /**
     * Toggles the visibility of the "Read More" link for the product description.
     */
    toggleReadMoreLink() {
      this.showReadMoreLink = !this.showReadMoreLink;
    },

    /**
     * Updates the selected quantity based on the user's input.
     * @param {number} newSelectQuantity - The new selected quantity.
     */
    updateSelectQuantity(newSelectQuantity) {
      this.selectQuantity = newSelectQuantity;
    },

    /**
     * Subscribes the user to receive updates about the product.
     * Validates the subscriber's email and sends a subscription request to the server.
     * Displays success or error messages using Toastr.
     * @returns {boolean} - True if the subscription is successful, otherwise false.
     */
    async subscribeToProduct() {
      // Validate the subscriber's email before proceeding
      if (!isValidEmail(this.subscriberEmail)) {
        return false;
      }

      try {
        // Send a subscription request to the server
        const response = await axios.post('/api/product-subscribe', {
          product_id:         this.product.id,
          email:              this.subscriberEmail,
          gRecaptchaResponse: this.gRecaptchaResponse,
        });

        // Clear the subscriber's email and show success message
        this.subscriberEmail = '';
        window.toastr.success(response.data.message);
      } catch (error) {
        // Show error message in case of subscription failure
        window.toastr.error(error.response.data.message);
      }
    }
  }
};
</script>
